import { getApiErrorMessage } from "../../utils/apiErrorUtils";

export interface ApiErrorInterface<T> {
    message: string;
    data: T | undefined;
}

export interface SerializedApiError<T> extends ApiErrorInterface<T> {
    type: string;
}

export class ApiError<T> extends Error implements ApiErrorInterface<T> {
    data: T | undefined;

    constructor(message?: string|null|undefined, data?: T) {
        super(message ?? "An error occurred while processing your request. Please try again.");
        this.data = data;
    }

    serialize() {
        return {
            type: this.constructor.name,
            message: this.message,
            data: this.data,
        };
    }

    static deserialize<T>(serialized: SerializedApiError<T>): ApiError<T>|null {
        if (!serialized || !serialized.type) {
            return null;
        }

        const types = [
            ApiError,
            NotFoundError,
            TooManyRequestsError,
            RequestTimedOutError,
            GatewayTimeoutError,
            UnprocessableContentError,
            MissingFrontEndStateError,
            LockedFilingWindowError,
        ];
        for (const type of types) {
            if (type.name === serialized.type) {
                return new type(serialized.message, serialized.data);
            }
        }
        return null;
    }

    toString() {
        return this.message;
    }
}

export class NotFoundError extends ApiError<undefined> {
    static test = (error: any) => error.response && error.response.status === 404;
    constructor(message?: string|null|undefined) {
        super(message ?? "The requested resource could not be found.");
    }

    toString() {
        return this.message;
    }
}

export class UnauthenticatedError extends ApiError<undefined> {
    static testMessage = (message: string) => message === 'Unauthenticated.';
    constructor(message?: string|null|undefined) {
        super(message ?? "You are not authorized to access this resource.");
    }

    toString() {
        return this.message;
    }
}

export class MissingFrontEndStateError extends ApiError<undefined> {
    static test = (error: any) => error.response && error.response.status === 404 && error.response.data && error.response.data.message === 'no_entity';
    constructor(message?: string|null|undefined) {
        super(message ?? "Frontend state appears to be missing.");
    }

    toString() {
        return this.message;
    }
}

export class TooManyRequestsError extends ApiError<undefined> {
    static test = (error: any) => error.response && error.response.status === 429;
    constructor(message?: string|null|undefined) {
        super(message ?? "We've detected an unusual amount of requests from your browser. Please wait a moment and try again.");
    }

    toString() {
        return this.message;
    }
}

export class RequestTimedOutError extends ApiError<undefined> {
    static test = (error: any) => error.code === 'ECONNABORTED' && error.message.includes('timeout');
    constructor(message?: string|null|undefined) {
        super(message ?? "Request timed out. You may have uploaded a very large file or more than one page of your form. Please ensure you are only uploading one page, otherwise there may be a connection issue and you may need to check your internet connection.");
    }

    toString() {
        return this.message;
    }
}

export class GatewayTimeoutError extends ApiError<undefined> {
    static test = (error: any) => error.hasOwnProperty('response') && error.response.status === 504;
    constructor(message?: string|null|undefined) {
        super(message ?? "There was an unexpected error processing your request. You may try again, but if you receive this message multiple times, there may be a technical outage with our services. This error has been logged and will be investigated.");
    }

    toString() {
        return this.message;
    }
}

export class UnprocessableContentError<T> extends ApiError<T> {
    static test = (error: any) => error.hasOwnProperty('response') && error.response.status === 422;
    constructor(message?: string|null|undefined, data?: T) {
        super(message ?? "There was an error processing the content of your request.", data);
    }

    toString() {
        return this.message;
    }
}

export class LockedFilingWindowError extends ApiError<undefined> {
    static test = (error: any) => error.hasOwnProperty('response') && error.response.status === 423;
    constructor(message?: string|null|undefined) {
        super(message ?? "The filing window is now closed. If you believe this is an error, please contact support.");
    }

    toString() {
        return this.message;
    }
}

export const getError = (error: any, fallback?: string): ApiError<undefined> => {
    fallback ??= "An error occurred while processing your request. Please try again.";
    if (RequestTimedOutError.test(error)) {
        return new RequestTimedOutError();
    }
    if (TooManyRequestsError.test(error)) {
        return new TooManyRequestsError();
    }
    if (LockedFilingWindowError.test(error)) {
        return new LockedFilingWindowError();
    }
    // Check for missing frontend error before NotFoundError
    // because it's a subset of a NotFoundError
    if (MissingFrontEndStateError.test(error)) {
        return new MissingFrontEndStateError();
    }
    if (NotFoundError.test(error)) {
        return new NotFoundError();
    }
    if (GatewayTimeoutError.test(error)) {
        return new GatewayTimeoutError();
    }
    const response = error.response;
    if (UnprocessableContentError.test(error)) {
        let message;
        console.log('unprocessable content error', response);
        if (response && response.data) {
            if (response.data.data && response.data.data.errors && response.data.data.errors.uploaded_document) {
                message = response.data.data.errors.uploaded_document.join("\n");
            } else {
                message = getApiErrorMessage(response.data.data ? response.data.data : response.data, response.data.message ?? fallback);
            }
        }
        return new UnprocessableContentError(message, response?.data?.data);
    }
    let message, data;
    if (response && response.data) {
        if (response.data.message) {
            message = response.data.message;
            data = response.data.data ? response.data.data : response.data;
        } else if (response.data.data) {
            message = getApiErrorMessage(response.data.data, fallback);
            data = response.data.data;
        } else {
            message = getApiErrorMessage(response.data, fallback);
            data = response.data;
        }
    } else if (error.message) {
        message = error.message;
    } else {
        message = getApiErrorMessage(error, String(error));
    }

    if (UnauthenticatedError.testMessage(message)) {
        return new UnauthenticatedError();
    }

    return new ApiError(message, data);
    
}

export type FormErrors<T> = { [key in keyof T]?: string[] };

export interface FormErrorModel<T> {
    errors: FormErrors<T>;
}